import { Icon } from "@iconify/react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { TicketIntakeForm } from "@/features/tickets/intake/ticket-intake-form";
import { TicketIntakeLeaveDialog } from "@/features/tickets/intake/ticket-intake-leave-dialog";
import { useTicketIntakeNavigation } from "@/features/tickets/intake/use-ticket-intake-navigation";
import { useI18n } from "@/locales/i18n-provider";
import { useEmbeddedHost } from "../embedded-host-provider";
import { useUnsavedWork } from "../hooks/use-unsaved-work";
import { embeddedTicketReturnTo } from "./ticket-navigation";

export default function EmbeddedNewTicketPage() {
  const { t } = useI18n();
  const { businessContext, projectCode } = useEmbeddedHost();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = embeddedTicketReturnTo(location, Boolean(businessContext));
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [state, setState] = useState({ dirty: false, pending: false });
  const navigation = useTicketIntakeNavigation(state.dirty || state.pending);
  useUnsavedWork(state.dirty || state.pending);
  const returnToList = () => navigate(returnTo);
  useEffect(() => {
    if (!params.has("draft")) return;
    const next = new URLSearchParams(params);
    next.delete("draft");
    setParams(next, { replace: true });
  }, [params, setParams]);

  return (
    <section className="mx-auto flex min-h-full w-full max-w-6xl flex-col gap-6 px-4 py-5 sm:px-6">
      <header className="flex flex-col items-start gap-4">
        <Button
          aria-label={t("tickets.detail.returnToList")}
          size="sm"
          variant="ghost"
          className="self-start px-0 hover:bg-unset text-muted-foreground"
          disabled={state.pending}
          onClick={returnToList}
        >
          <Icon icon="tabler:arrow-left" />
          {t("tickets.detail.returnToList")}
        </Button>
        <h1 className="text-xl font-semibold text-foreground">{t("embedded.newTicket.title")}</h1>
      </header>
      {projectCode ? (
        <TicketIntakeForm
          key={projectCode}
          projectCode={projectCode}
          layout="workbench"
          initialContext={businessContext ? { hostLocator: businessContext } : undefined}
          onStateChange={setState}
          onCancel={returnToList}
          onCreated={(ticket) => {
            navigation.allowNavigation();
            setState({ dirty: false, pending: false });
            void queryClient.invalidateQueries({ queryKey: ["tickets"] });
            toast.success(t("tickets.new.createdToast"));
            void navigate({ pathname: `/embedded/tickets/${ticket.id}`, search: location.search }, { replace: true, state: { returnTo } });
          }}
        />
      ) : (
        <p role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          {t("embedded.newTicket.projectMissing")}
        </p>
      )}
      <TicketIntakeLeaveDialog blocker={navigation.blocker} pending={state.pending} />
    </section>
  );
}
